import {Trans} from '@lingui/macro';
import React, {useCallback, useMemo} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../../../../models/api/customFieldModel';
import {getCustomFieldFromResultType} from '../../../editors/expressions/expressionAtomHelpers';
import {ClientAtom, ClientFunctionAtom} from '../../../editors/expressions/expressionAtomModel';
import {getPreviousAtomResultType} from '../../../editors/expressions/expressionInferHelpers';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {useCustomFields} from '../hooks/useCustomFields';
import {defaultDropdownWidth, GenericSingleParameterProps} from './genericParameter';

/*
 * Types.
 */

interface CustomFieldParameterProps extends Omit<GenericSingleParameterProps<CustomFieldModel>, 'onChange'> {
  customFieldType?: CustomFieldsEnum;
  customFieldResourceType?: CustomFieldResourceTypesEnum;
  onCustomFieldChange: HandlerOf<number>;
  atom?: ClientAtom | ClientFunctionAtom;
}

/*
 * Component.
 */

export const CustomFieldParameter = ({
  customFieldType,
  customFieldResourceType = CustomFieldResourceTypesEnum.CARD,
  onCustomFieldChange,
  atom,
  ...rest
}: CustomFieldParameterProps) => {
  const customFields = useCustomFields(customFieldResourceType);
  const expressionsContext = useExpressionsContext();

  const items = useMemo(() => {
    if (atom) {
      return getCustomFieldFromResultType(getPreviousAtomResultType(atom), expressionsContext);
    }

    if (!customFieldType) {
      return customFields;
    }

    return customFields.filter((field) => field.type === customFieldType);
  }, [customFieldType, customFields, atom]);

  const onChange = useCallback(
    (item: OrNull<SelectedDropdownItem<CustomFieldModel>>) => {
      if (!item || isExpressionRef(item)) {
        return;
      }

      onCustomFieldChange(item);
    },
    [onCustomFieldChange],
  );

  return (
    <SingleChoiceDropdown
      {...rest}
      onChange={onChange}
      items={items}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.name}
      serializeItemForSearch={(item) => item.name}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-custom-field-param-placeholder">Select custom field</Trans>}
      title={
        rest.onSelectCreateExpression ? <Trans id="rule-editor-custom-field-group">Custom Field</Trans> : null
      }
    />
  );
};
